<template>
<TheStdLayout :title="$tc('Observation', 2)">

	<div class="px-4 py-6 mx-auto sm:px-6 md:px-8">
		<FloatingPlusButton :pulsate="!observationsStore.loading && observationsStore.items.length === 0 ? true : false" @push="handleObservationCreateRequest" />

		<ObservationsFilter class="print:hidden" :showLastSchoolYearPreset="false" />

		<EmptyState v-if="loaded && !observationsStore.loading && observationsStore.items.length === 0"
			:title="$t('No observations found for this period')"
		/>

		<div v-else-if="observationsStore.loading" class="flex justify-center py-12">
			<span class="loading loading-spinner loading-sm"></span>
		</div>

		<ObservationsList v-else
			:observations="observationsStore.items"
			:sortOrder="observationsStore.order"
			@sortOrderChanged="observationsStore.applyOrder"
			:classroomId="classroomsStore.currentClassroom?.id ?? null"
		/>

		<Pagination v-if="observationsStore.page" :page="observationsStore.page" @requestPage="observationsStore.goToPage"  />
  	</div>

	<ObservationEditor v-if="showObservationEditor" @requestClose="closeObservationForm" />

	<PupilGoalEvaluation
		v-if="newObservationGoals.length > 0"
		:pupil="newObservationPupil"
		:goals="newObservationGoals"
		@requestClose="closeGoalEvaluator"
	>
		<template #topMessage>
			<div class="flex items-start p-2 mb-2 bg-green-100 text-success-content rounded">
				<p>{{ $t('Observation successfully saved') }}</p>
			</div>
			<div class="flex items-start gap-3 p-2 mb-8 font-bold">
				<p>{{ $t('Evaluate the activity/lesson goals right now?') }}</p>
			</div>
		</template>
	</PupilGoalEvaluation>

</TheStdLayout>
</template>

<script lang="ts">
import { mapStores } from "pinia";
import { useObservationsStore } from "@/stores/Observations.store";
import { usePupilActivitiesStore } from "@/stores/PupilActivities.store";
import { useOrganisationsStore } from "@/stores/Organisations.store";
import { useClassroomsStore } from "@/stores/Classrooms.store";

import { Observation } from "@/models/Observation.model";
import { Goal } from "@/models/Goal.model";
import { Pupil } from "@/models/Pupil.model";

import ObservationEditor from "@/components/ObservationEditor.vue";
import ObservationsFilter from "./ObservationsFilter.vue";
import PupilGoalEvaluation from "@/components/PupilGoalEvaluation.vue";
import ObservationsList from "@/components/ObservationsList.vue";
import EmptyState from "@/components/ui/EmptyState.vue";
import FloatingPlusButton from "@/components/ui/FloatingPlusButton.vue";
import Pagination from "@/components/ui/Pagination.vue";


export default
{
	data() {
		return {
			loaded: false,
			showObservationEditor: false,
			newObservationGoals: [] as Goal[],
			newObservationPupil: null as Pupil | null,
		};
	},

	components: {
		ObservationsList,
		ObservationEditor,
		ObservationsFilter,
		PupilGoalEvaluation,
		EmptyState,
		FloatingPlusButton,
		Pagination,
	},

	computed: {
		...mapStores(
			usePupilActivitiesStore,
			useObservationsStore,
			useOrganisationsStore,
			useClassroomsStore,
		),
	},

	async mounted() {
		await this.load();
	},

	methods: {

		async load() {
			await Promise.all([
				this.observationsStore.loadFirstPage(),
				this.pupilActivitiesStore.loadActivityOnDate(new Date())
			]);
			this.loaded = true;
		},

		handleObservationCreateRequest() {
			this.showObservationEditor = true;
		},

		closeObservationForm(observation: Observation | null) {
			this.showObservationEditor = false;

			if(!observation) return;

			// Only the school's curriculum goals may be evaluated; none without a curriculum.
			this.newObservationGoals = this.organisationsStore.curriculumGoals(observation.relatedGoals);
			this.newObservationPupil = observation.pupil;
		},

		closeGoalEvaluator() {
			this.newObservationPupil = null;
			this.newObservationGoals = [];
		},
	},
 }

</script>
